import React, { useCallback, useMemo, useState } from 'react';
import { useAuth, useMatchPlay } from '@shootout-comps/core';
import type { AudioAdapter } from '@shootout-comps/core';
import { useMatchSocket, useConnectionStatus } from '../../adapters/webSocket';
import { useLang } from '@shootout-comps/i18n';
import MatchScoringSurface from '../../components/MatchScoringSurface';

interface Props { matchId: string; onBack: () => void; }

// A league match is scored through the exact same /api/matches/:matchId/* engine as a
// tournament match (see apps/api/src/match-parent.ts) — this page wraps the shared
// useMatchPlay hook unchanged, and renders the same shared MatchScoringSurface that
// MatchPlayPage.tsx uses, trimmed via config props to only the features ever reachable for a
// match: always isStandard (real leagues have no shot clock), always single-frame (bestOf is
// always 1), no tournament-wide pause-all/debug-jump/restart concerns.
export default function FixtureFramePage({ matchId, onBack }: Props) {
  const { getAccessToken } = useAuth();
  const { t } = useLang();

  // Sounds are always off for a league match (no shot clock, no clock cues) — no-op rather than
  // wiring up the mute-toggle machinery MatchPlayPage needs for its timed formats.
  const audio = useMemo<AudioAdapter>(() => ({
    playBeep: () => undefined, playCountdownBeeps: () => undefined, playBuzzer: () => undefined, playMatchExpiry: () => undefined,
    speakAnnouncement: () => undefined,
    silenceAll: () => undefined, unsilence: () => undefined,
  }), []);

  const [forcedSpectator, setForcedSpectator] = useState(false);
  const [spectatorMode, setSpectatorMode]     = useState(false);
  const [widescreenMode, setWidescreenMode]   = useState(false);

  const onRoleAssigned = useCallback((role: 'scorer' | 'spectator') => {
    const isSpectator = role === 'spectator';
    setForcedSpectator(prevForced => {
      if (isSpectator) {
        setSpectatorMode(true);
      } else if (prevForced) {
        setSpectatorMode(false);
      }
      return isSpectator;
    });
  }, []);

  const mp = useMatchPlay({
    matchId,
    apiBaseUrl: '',
    getAccessToken,
    audio,
    isStandardTournament: true,
    t,
    useMatchSocket,
    useConnectionStatus,
    onRoleAssigned,
  });

  return (
    <MatchScoringSurface
      mp={mp}
      t={t}
      backLabel={t.backToFixture}
      onBack={onBack}
      isStandard
      spectatorFmt="standard"
      spectatorMode={spectatorMode}
      setSpectatorMode={setSpectatorMode}
      widescreenMode={widescreenMode}
      setWidescreenMode={setWidescreenMode}
      forcedSpectator={forcedSpectator}
    />
  );
}
